<script lang="ts" setup>
import Cases from '~/components/reusables/Cases.vue'

definePageMeta({
  layout: 'menu',
})

const {
  public: { siteUrl, appLink },
} = useRuntimeConfig()

const breakpoints = useBreakpoints({
  laptop: 1024,
  desktop: 1280,
})

const { t, locale } = useI18n()
const localeRoute = useLocaleRoute()

useSeoMeta({
  title: t('real-world-on-chain'),
  description: t('tokenized-and-ai-powered-events'),
  ogTitle: t('real-world-on-chain'),
  ogDescription: t('tokenized-and-ai-powered-events'),
  ogImage: `${siteUrl}/images/head/snippet_screen.png`,
  ogImageWidth: '1000',
  ogImageHeight: '300',
  ogImageType: 'image/png',
  ogType: 'website',
  twitterTitle: t('real-world-on-chain'),
  twitterDescription: t('tokenized-and-ai-powered-events'),
  twitterImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})

const smallerThanXl = breakpoints.smaller('desktop')
const largerThanXl = breakpoints.greater('desktop')

const isRuLocale = computed(() => locale.value === 'ru')
const isNotRuLocale = computed(() => locale.value !== 'ru')

const isDevelopment = computed(() => {
  return process.env.NODE_ENV === 'development'
})

const swiperBreakpoints = computed(() => ({
  320: { slidesPerView: 1.1 },
  375: { slidesPerView: 1.2 },
  480: { slidesPerView: 1.5 },
  640: { slidesPerView: 1.8 },
  800: { slidesPerView: 2.2 },
  1000: { slidesPerView: 2.5 },
  1100: { slidesPerView: 2.8 },
  1280: { slidesPerView: 3 },
}))

const partnerClasses: { [key: number]: string } = {
  1: 'min-w-48.5 max-w-48.5',
  2: 'min-w-39.5 max-w-39.5',
  3: 'min-w-44 max-w-44',
  4: 'min-w-36.5 max-w-36.5',
  5: 'min-w-51.5 max-w-51.5',
  6: 'min-w-12.5 max-w-12.5',
  7: 'min-w-14 max-w-14',
  8: 'min-w-32.5 max-w-32.5',
}

const partners = [
  {
    id: 1,
    img: '/images/home/icons/google-cloud.png',
  },
  {
    id: 2,
    img: '/images/home/icons/open-ai.png',
  },
  {
    id: 3,
    img: '/images/home/icons/digital-ocean.png',
  },
  {
    id: 4,
    img: '/images/home/icons/segment.png',
  },
  {
    id: 5,
    img: '/images/home/icons/tencent-cloud.png',
  },
  {
    id: 6,
    img: '/images/home/icons/aws.png',
  },
  {
    id: 7,
    img: '/images/home/icons/sui-logo.png',
  },
  {
    id: 8,
    img: '/images/home/icons/starknet-logo.png',
  },
]

const features = ref([
  {
    id: 1,
    title: t('tokenize-any-real-world-location'),
    img: breakpoints.desktop.value
      ? '/images/home/tokenized_location_laptop.png'
      : '/images/home/tokenized_location.png',
    link: '/token-gating',
  },
  {
    id: 2,
    title: t('distribute-on-chain-bounties-for-offline-activity'),
    img: '/images/home/onchain_bounties.png',
    link: '/chainatlas',
  },
  {
    id: 3,
    title: t('generate-digital-keys-and-nft-tickets'),
    img: '/images/home/nft_tickets.png',
    link: '/nft-tickets',
  },
  {
    id: 4,
    title: t('innovate-raffles-and-community-management-with-ai'),
    img: '/images/home/ai_raffles.png',
    link: '/ai',
  },
  {
    id: 5,
    title: t('enable-location-based-rewards'),
    img: '/images/home/location_rewards.png',
    link: '/engagement',
  },
])

const useCases = [
  {
    id: 1,
    title: t('bounty-marketplace-for-real-world-activities'),
    image: '/images/home/icons/bounty-marketplace.png',
  },
  {
    id: 2,
    title: t('nft-sbt-tickets-for-events'),
    image: '/images/home/icons/nft-sbt-tickets.png',
  },
  {
    id: 3,
    title: t('ai-raffles-and-community-engagement'),
    image: '/images/home/icons/ai-raffles.png',
  },
  {
    id: 4,
    title: t('smart-space-access-with-depin'),
    image: '/images/home/icons/smart-access-depin.png',
  },
  {
    id: 5,
    title: t('content-gating'),
    image: '/images/home/icons/content-gating.png',
  },
  {
    id: 6,
    title: t('physical-goods-verification'),
    image: '/images/home/icons/physical-verification.png',
  },
]

const INN_UTIL = [
  {
    year: 2026,
    title: '',
    quarters: {
      Q2Q3: [
        {
          id: 11,
          text: t('tokenized-gyms-and-classes-vertical-launch'),
          checked: false,
        },
        {
          id: 11,
          text: t('web3-hospitality-syndicate-formation'),
          checked: false,
        },
        {
          id: 11,
          text: t('location-based-bounty-marketplace-rollout'),
          checked: false,
        },
        {
          id: 11,
          text: t('100-000-active-promoters-onboarded'),
          checked: false,
        },
        { id: 11, text: t('us-enterprise-venue-partnerships'), checked: false },
      ],

      Q4: [
        { id: 11, text: t('50-000-venues-globally-onboarded'), checked: false },
        { id: 11, text: t('10m-rewards-distributed'), checked: false },
        { id: 11, text: t('60-venue-retention-rate'), checked: false },
      ],
    },
  },

  {
    year: 2025,
    title: t('innovation-and-utility'),
    quarters: {
      Q1: [
        {
          id: 1,
          text: t('worldwide-airdrop-with-offline-claiming'),
          checked: true,
        },
        { id: 2, text: t('ai-raffles-and-ai-agents'), checked: true },
        { id: 3, text: t('integration-with-analog'), checked: true },
        { id: 4, text: t('ton-launch'), checked: true },
        { id: 5, text: 'TGE', checked: false },
        { id: 6, text: t('sui-launch'), checked: true },
        { id: 7, text: t('location-based-bounty-marketplace'), checked: false },
      ],
      Q2Q3: [
        { id: 8, text: t('depin-rentals'), checked: false },
        { id: 9, text: t('scientific-research-tokenization'), checked: false },
        { id: 10, text: t('gamification'), checked: false },
        { id: 11, text: t('expo-osaka-activation'), checked: false },
        { id: 11, text: t('full-mcp-coverage'), checked: false },
        { id: 11, text: t('40-new-app-features'), checked: false },
        {
          id: 11,
          text: t('belong-checkin-docs-and-smart-contracts'),
          checked: false,
        },
        {
          id: 11,
          text: t('activations-at-consensus-and-token2049'),
          checked: false,
        },
        { id: 11, text: t('burnermap-com-migration'), checked: false },
        { id: 11, text: t('third-audit-completion'), checked: false },
        { id: 11, text: t('ai-chat-mode-beta'), checked: false },
      ],
      Q4: [
        { id: 11, text: t('tge-and-bnb-chain-deployment'), checked: false },
        { id: 11, text: t('checkin-platform-launch-post-tge'), checked: false },
        { id: 11, text: t('circular-long-economy-live') },
        { id: 11, text: t('venues-get-verified-customers'), checked: false },
        {
          id: 11,
          text: t('customers-spend-long-3-discount-referral-rewards'),
          checked: false,
        },
        {
          id: 11,
          text: t('long-flows-into-venue-reward-pool'),
          checked: false,
        },
        { id: 11, text: t('promoters-earn-bounties-of-spend'), checked: false },
        {
          id: 11,
          text: t('stakers-share-30-revenue-venues-cut-fees-by-staking'),
          checked: false,
        },
        { id: 11, text: '' },
        {
          id: 11,
          text: t('target-5-000-venues-onboarded-by-q1-2026'),
          checked: false,
        },
      ],
    },
  },
  {
    year: 2024,
    title: t('ai-multi-chain-growth'),
    quarters: {
      Q4: [
        { id: 12, text: t('advanced-whitelabel'), checked: true },
        { id: 13, text: t('devcon-activation'), checked: true },
        { id: 14, text: t('geo-based-minting'), checked: true },
        { id: 15, text: t('second-audit-by-hacken'), checked: true },
      ],
      Q3: [
        { id: 16, text: t('ai-driven-templates'), checked: true },
        { id: 17, text: t('dynamic-pricing'), checked: true },
        { id: 18, text: t('api-for-gaming'), checked: true },
      ],
      Q2: [
        { id: 19, text: t('multi-chain-x-fiat-payments'), checked: true },
        { id: 20, text: t('sdk-launch'), checked: true },
        {
          id: 21,
          text: t('ai-generated-user-and-brand-landing-pages'),
          checked: true,
        },
      ],
      Q1: [
        {
          id: 22,
          text: t('worldwide-airdrop-with-offline-claiming'),
          checked: true,
        },
        { id: 23, text: t('ai-raffles-and-ai-agents-0'), checked: true },
        { id: 24, text: t('integration-with-analog-0'), checked: true },
      ],
    },
  },
  {
    year: 2023,
    title: t('scaling-and-security'),
    quarters: {
      Q3Q4: [
        { id: 25, text: t('ai-moderation'), checked: true },
        { id: 26, text: t('security-scaling'), checked: true },
        { id: 27, text: t('access-tokenization-at-scale'), checked: true },
      ],
      Q1Q2: [
        {
          id: 28,
          text: t(
            'chains-expansion-eth-polygon-base-starknet-skale-and-celo-networks'
          ),
          checked: true,
        },
        { id: 29, text: t('transaction-upgrades'), checked: true },
      ],
    },
  },
  {
    year: 2022,
    title: t('web3-expansion'),
    quarters: {
      0: [
        { id: 30, text: t('nft-collections'), checked: true },
        { id: 31, text: t('metamask-support'), checked: true },
        { id: 32, text: t('profile-enhancements'), checked: true },
        { id: 33, text: t('discovery-tools'), checked: true },
      ],
    },
  },
  {
    year: 2021,
    title: 'FOUNDATION',
    quarters: {
      0: [
        { id: 34, text: t('infra-setup'), checked: true },
        { id: 35, text: t('multi-platform-rollout'), checked: true },
        { id: 36, text: t('social-graph-launch'), checked: true },
        { id: 37, text: t('event-tools-integration'), checked: true },
      ],
    },
  },
]

const product = [
  {
    id: 1,
    title: t('live-application-in-app-store-and-google-play'),
    image: '/images/home/icons/apple-google.png',
  },
  {
    id: 2,
    title: t(
      'zero-friction-access-nft-sbt-purchase-in-30-seconds-without-a-crypto-wallet'
    ),
    image: '/images/home/icons/zero-friction.png',
  },
  {
    id: 3,
    title: t('gasless-transactions'),
    image: '/images/home/icons/gasless-transactions.png',
  },
  {
    id: 4,
    title: t(
      'multichain-currently-operational-on-eth-pol-skale-base-blast-6-more-to-come'
    ),
    image: '/images/home/icons/multichain.png',
  },
  {
    id: 5,
    title: t('apple-pay-and-google-pay-integration'),
    image: '/images/home/icons/app-apple-google.png',
  },
]
const product1 = [
  {
    id: 1,
    title: t(
      'ai-agents-for-higher-clv-through-community-engagement-content-and-product-discovery'
    ),
    image: '/images/home/icons/ai-engagement.png',
  },
  {
    id: 2,
    title: t(
      'robust-api-and-sdk-powering-the-next-wave-of-gated-applications-built-on-long-protocol'
    ),
    image: '/images/home/icons/api-sdk.png',
  },
  {
    id: 3,
    title: t('location-based-tokens-and-task-bounties-ai-powered-engagement'),
    image: '/images/home/icons/location-tokens.png',
  },
  {
    id: 4,
    title: t('full-whitelabel-capability'),
    image: '/images/home/icons/whitelabel.png',
  },
]
const collabsAndCommunity = [
  {
    collabs: [
      { id: 1, name: 'Alphabot', image: '/images/home/collabs/alphabot.png' },
      { id: 2, name: 'Analog', image: '/images/home/collabs/analog.png' },
      {
        id: 3,
        name: 'Anya frens',
        image: '/images/home/collabs/anya_frens.png',
      },
      { id: 4, name: 'Apelist', image: '/images/home/collabs/apelist.png' },
      { id: 5, name: 'Ash hub', image: '/images/home/collabs/ash_hub.png' },
      {
        id: 6,
        name: 'Blvck paris',
        image: '/images/home/collabs/blvck_paris.png',
      },
      { id: 7, name: 'Circle', image: '/images/home/collabs/circle.png' },
      {
        id: 8,
        name: 'Connectivity',
        image: '/images/home/collabs/connectivity.png',
      },
      { id: 9, name: 'Clokies', image: '/images/home/collabs/clokies.png' },
      {
        id: 10,
        name: 'Coin telegraph',
        image: '/images/home/collabs/cointelegraph.png',
      },
      {
        id: 11,
        name: 'Cricket fly',
        image: '/images/home/collabs/cricket_fly.png',
      },
      {
        id: 12,
        name: 'Degen dao',
        image: '/images/home/collabs/degen_dao.png',
      },
      { id: 13, name: 'Dislands', image: '/images/home/collabs/dislands.png' },
      { id: 14, name: 'Embassy', image: '/images/home/collabs/embassy.png' },
      { id: 15, name: 'Gate', image: '/images/home/collabs/gate.png' },
      {
        id: 16,
        name: 'Generative dungeon',
        image: '/images/home/collabs/generative_dungeon.png',
      },
      {
        id: 17,
        name: 'Grind hub',
        image: '/images/home/collabs/grind_hub.png',
      },
      {
        id: 18,
        name: 'Hexagon labs',
        image: '/images/home/collabs/hexagon_labs.png',
      },
      {
        id: 19,
        name: 'Intoverse',
        image: '/images/home/collabs/intoverse.png',
      },
      {
        id: 20,
        name: 'Jerry alpha',
        image: '/images/home/collabs/jerry_alpha.png',
      },
      {
        id: 21,
        name: 'Life’s Meta',
        image: '/images/home/collabs/lifes_meta.png',
      },
      {
        id: 22,
        name: 'Magic store',
        image: '/images/home/collabs/magic_store.png',
      },
      {
        id: 23,
        name: 'Metamerge',
        image: '/images/home/collabs/metamerge.png',
      },
    ],
  },
  {
    collabs: [
      {
        id: 24,
        name: 'Miningverse',
        image: '/images/home/collabs/miningverse.png',
      },
      {
        id: 25,
        name: 'Nft bridge',
        image: '/images/home/collabs/nft_bridge.png',
      },
      { id: 26, name: 'Nft hub', image: '/images/home/collabs/nft_hub.png' },
      {
        id: 27,
        name: 'Nunu spirits',
        image: '/images/home/collabs/nunu_spirits.png',
      },
      { id: 28, name: 'Obvious', image: '/images/home/collabs/obvious.png' },
      {
        id: 29,
        name: 'Ordinal eggs',
        image: '/images/home/collabs/ordinal_eggs.png',
      },
      {
        id: 30,
        name: 'Raiders lab',
        image: '/images/home/collabs/raiders_lab.png',
      },
      {
        id: 31,
        name: 'Sharp trade',
        image: '/images/home/collabs/sharp_trade.png',
      },
      { id: 32, name: 'Skale', image: '/images/home/collabs/skale.png' },
      { id: 33, name: 'Syndika', image: '/images/home/collabs/syndika.png' },
      {
        id: 34,
        name: '$pirit gate$',
        image: '/images/home/collabs/spirit_gates.png',
      },
      { id: 35, name: 'Tangerine', image: '/images/home/collabs/tangerin.png' },
      {
        id: 36,
        name: 'Ton capital',
        image: '/images/home/collabs/ton_capital.png',
      },
      {
        id: 37,
        name: 'Top tier signal',
        image: '/images/home/collabs/top_tier_signal.png',
      },
      {
        id: 38,
        name: 'Tropa da rih',
        image: '/images/home/collabs/tropa_da_rih.png',
      },
      {
        id: 39,
        name: 'Ultiverse',
        image: '/images/home/collabs/ultiverse.png',
      },
      {
        id: 40,
        name: 'Versa web3',
        image: '/images/home/collabs/versa_web3.png',
      },
      { id: 41, name: 'Word4zz', image: '/images/home/collabs/word4zz.png' },
      {
        id: 42,
        name: 'Xcelerator',
        image: '/images/home/collabs/xcelerator.png',
      },
      { id: 43, name: 'Xdao', image: '/images/home/collabs/xdao.png' },
      { id: 44, name: 'Xhashtag', image: '/images/home/collabs/xhashtag.png' },
      { id: 45, name: 'Zkgames', image: '/images/home/collabs/zkgames.png' },
      { id: 46, name: 'Zooverse', image: '/images/home/collabs/zooverse.png' },
    ],
  },
]

onMounted(() => {
  if (document.querySelector('script[data-spline]')) return

  const script = document.createElement('script')
  script.type = 'module'
  script.src = 'https://unpkg.com/@splinetool/viewer/build/spline-viewer.js'
  script.setAttribute('data-spline', 'true')
  script.onerror = () => console.error('Failed to load SplineViewer script')

  document.body.appendChild(script)
})

const belongTransforms = ref([
  {
    id: 1,
    title: t('brands'),
    description: t('brands_text'),
    image: '/images/home/brands.png',
  },
  {
    id: 2,
    title: t('communities'),
    description: t('communities_text'),
    image: '/images/home/communities.png',
  },
  {
    id: 3,
    title: t('users'),
    description: t('users_text'),
    image: '/images/home/users.png',
  },
  {
    id: 4,
    title: t('developers'),
    description: t('developers_text'),
    image: '/images/home/developers.png',
  },
])

const exchanges = [
  {
    id: 1,
    img: '/images/home/binance.png',
    name: 'Binance',
    link: 'https://x.com/binancewallet/status/1985618226831880269',
  },
  {
    id: 2,
    img: '/images/home/gate.png',
    name: 'Gate',
    link: 'https://www.gate.com/announcements/article/47986',
  },
]
</script>

<template>
  <div>
    <div class="flex flex-col gap-3.5 text-center relative">
      <div class="h-166 relative overflow-hidden z-2 lg:h-175">
        <NuxtImg
          fetchpriority="high"
          format="webp"
          src="/images/home/gradient.png"
          alt=""
          width="1343"
          height="1045"
          class="object-cover h-full absolute z-1"
        />
        <ClientOnly v-if="!isDevelopment">
          <div
            class="absolute left-0 w-500 transform -translate-x-1/2 h-full z-2 lg:translate-0 lg:w-auto lg:right-0"
          >
            <spline-viewer
              url="https://prod.spline.design/k-xYdIMLBOGBJGqh/scene.splinecode"
            />
          </div>
        </ClientOnly>
        <div
          class="h-25.5 w-full bg-gradient-to-t from-[#151515] to-transparent absolute bottom-0 z-4"
        />
      </div>

      <div
        class="absolute px-5 flex flex-col gap-3.5 z-5 left-0 right-0 max-lg:bottom-0 lg:top-87.5 lg:left-34.5 lg:right lg:text-left"
      >
        <h1
          class="font-extrabold leading-12 uppercase"
          :class="{
            'text-[2.5rem]': isRuLocale,
            'text-[3rem] lg:text-[3.375rem]': isNotRuLocale,
          }"
        >
          {{ t('real-world-on-chain') }}
        </h1>

        <h2 class="text-xl tracking-wide uppercase leading-6 lg:max-w-175">
          {{
            t(
              'tokenized-and-ai-powered-events-properties-and-engagement-campaigns'
            )
          }}
        </h2>

        <div
          class="flex flex-row gap-5 justify-center lg:justify-start lg:mt-8"
        >
          <ReusablesInstallButtons />
        </div>
        <!-- <button
          class="p-0.25 rounded-full w-full max-w-97 mx-auto lg:hidden"
          style="
            background-image: linear-gradient(
              85.11deg,
              #7540ee -2.65%,
              #4467ff 47.09%,
              #ff8fe9 93.65%
            );
          "
        >
          <div class="bg-black rounded-full font-medium text-base py-2.5">
            Get Started
          </div>
        </button> -->
      </div>
    </div>

    <div
      class="flex flex-col justify-center items-center gap-5 mx-auto px-5 mt-15 lg:mt-0 min-[860px]:flex-row min-[860px]:px-0"
    >
      <NuxtLink
        v-for="ex in exchanges"
        :key="ex.id"
        :to="ex.link"
        target="_blank"
      >
        <NuxtImg
          :key="ex.id"
          :src="ex.img"
          class="border-1 border-solid border-[#40424F] rounded-xl"
        />
      </NuxtLink>
    </div>

    <div class="max-w-360 mx-auto relative">
      <p
        class="text-[#7540EE] text-base tracking-wider text-center pb-7.5 uppercase pt-11"
      >
        {{ t('partners-and-grants') }}
      </p>

      <div
        class="carousel max-w-360 overflow-hidden flex mx-auto relative mt-14"
      >
        <div
          class="w-30 h-full absolute left-0 top-0 m-auto bg-gradient-to-r z-9 from-[#151515] to-transparent"
        />
        <div
          class="w-30 h-full absolute right-0 top-0 m-auto bg-gradient-to-r z-9 to-[#151515] from-transparent"
        />
        <div class="flex flex-row items-center gap-16.5 carousel-track pr-16.5">
          <div
            v-for="item in partners"
            :key="item.id"
            :class="partnerClasses[item.id]"
          >
            <NuxtImg :src="item.img" class="object-contain opacity-40 h-7.5" />
          </div>
        </div>
        <div class="flex flex-row items-center gap-16.5 carousel-track pr-16.5">
          <div
            v-for="item in partners"
            :key="item.id"
            :class="partnerClasses[item.id]"
          >
            <NuxtImg :src="item.img" class="object-contain opacity-40 h-7.5" />
          </div>
        </div>
      </div>
    </div>
    <div class="pt-10 lg:pt-19">
      <ClientOnly>
        <div
          v-if="largerThanXl"
          class="flex flex-row flex-wrap gap-3 max-w-268 mx-auto"
        >
          <NuxtLink
            :to="localeRoute(item.link)"
            v-for="item in features"
            :key="item.id"
            class="flex"
            :class="{
              'w-145 h-81': item.id === 1,
              'w-120 ': item.id === 2,
              'w-91 h-75': item.id === 3,
              'w-85.5 ': item.id === 4 || item.id === 5,
            }"
          >
            <div
              class="rounded-[1.25rem] relative w-full feature-gradient common-gradient"
            >
              <p
                class="font-medium text-xl tracking-wide uppercase left-6 top-6 right-10.5 absolute text-left leading-6"
                :class="{ 'max-w-75': item.id === 1 }"
              >
                {{ item.title }}
              </p>
              <NuxtImg
                loading="lazy"
                format="webp"
                class="w-full h-full object-cover rounded-[1.25rem]"
                alt=""
                :src="item.img"
              />
            </div>
          </NuxtLink>
        </div>
        <swiper-container
          v-if="smallerThanXl"
          :space-between="12"
          :pagination="{ clickable: true }"
          :slidesOffsetBefore="20"
          :slidesOffsetAfter="20"
          :breakpoints="swiperBreakpoints"
        >
          <swiper-slide v-for="(item, index) in features" :key="index">
            <NuxtLink :to="localeRoute(item.link)">
              <div
                class="p-0.25 rounded-[1.25rem] feature-gradient common-gradient"
              >
                <div class="relative h-117.5">
                  <p
                    class="font-medium text-[1.375rem] tracking-wide uppercase left-6 top-6 right-10.5 absolute text-left"
                  >
                    {{ item.title }}
                  </p>
                  <NuxtImg
                    loading="lazy"
                    format="webp"
                    class="w-full h-117.5 object-cover rounded-[1.25rem]"
                    :class="{
                      'object-[84%]': item.id === 2,
                      'object-left': item.id === 4,
                    }"
                    alt=""
                    :src="item.img"
                  />
                </div>
              </div>
            </NuxtLink>
          </swiper-slide>
        </swiper-container>
      </ClientOnly>
    </div>

    <div class="flex flex-col gap-12.5 pt-7.5 px-2.5 lg:gap-29.5 lg:pt-35.5">
      <div
        class="font-semibold uppercase leading-[3.125rem] text-center lg:max-w-250 lg:mx-auto lg:tracking-wide"
        :class="{
          'text-2xl': isRuLocale,
          'text-[2rem] lg:text-[2.75rem]': isNotRuLocale,
        }"
        style="
          background: linear-gradient(
              107.92deg,
              #4d61fd 37.78%,
              #bb2bbf 106.06%
            )
            text;
          -webkit-text-fill-color: transparent;
        "
      >
        {{ t('belong-transforms-any') }}
        <!-- <div
          class="min-w-14 h-full min-h-14 rounded-full max-h-14 bg-gradient-to-t to-[#FF92FF] from-[#8980FF]"
        /> -->
        {{
          t(
            'access-point-whether-physical-or-digital-into-programmable-tokens-empowering'
          )
        }}
      </div>

      <div
        class="mx-auto lg:flex lg:flex-row lg:items-start lg:gap-30 lg:relative lg:overflow-hidden"
      >
        <div class="flex gap-2.5 flex-row lg:flex-col lg:gap-15">
          <div
            v-for="item in belongTransforms.slice(0, 2)"
            :key="item.id"
            class="bg-custom-gradient h-auto text-center flex w-full max-w-50 px-2.5 min-[425px]:px-3.5 flex-col pb-1.5 lg:flex-col lg:items-start lg:text-left lg:![background:none] lg:px-0 w-full rounded-lg"
          >
            <NuxtImg
              loading="lazy"
              format="webp"
              :src="item.image"
              alt=""
              class="w-25 h-25 max-lg:mx-auto"
            />
            <div class="flex flex-col h-full lg:gap-2">
              <p
                class="uppercase font-semibold text-sm tracking-wide leading-tight break-all"
                :class="{
                  'min-[425px]:text-lg': isRuLocale,
                  'min-[425px]:text-xl': isNotRuLocale,
                }"
              >
                {{ item.title }}
              </p>
              <p
                class="text-[#969696] h-full flex text-sm items-center leading-tight tracking-wide text-xs min-[425px]:text-base break-words"
                style="word-break: break-word"
              >
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex items-center h-55.5 max-lg:relative lg:h-90.5">
          <div
            class="flex flex-row gap-17 justify-center w-full absolute lg:flex-col lg:top-15 lg:left-26 lg:gap-22.5 lg:w-auto top-0 -z-1"
          >
            <div
              v-for="(i, index) in 2"
              class="masked-element bg-[#7857c2] w-29 h-19 overflow-hidden lg:w-63.75 lg:h-21.25"
              :class="{
                '-scale-y-100 lg:scale-y-100': index === 0,
                '-scale-y-100 -scale-x-100 lg:scale-x-100': index === 1,
              }"
            >
              <div
                class="rotating-gradient-element w-58 h-58 lg:w-130 lg:h-130"
              />
            </div>
          </div>

          <NuxtImg
            loading="lazy"
            format="webp"
            src="/images/home/transforms.png"
            alt=""
            class="w-48 h-53.25 mx-auto lg:w-81.5 lg:h-90.5"
          />

          <div
            class="flex flex-row gap-17 justify-center w-full absolute max-lg:bottom-0 lg:flex-col lg:top-13 lg:right-47 lg:gap-22.5 lg:w-auto -z-1"
          >
            <div
              v-for="(i, index) in 2"
              class="masked-element bg-[#7857c2] w-29 h-19 overflow-hidden lg:w-45 lg:h-21.25"
              :class="{
                'lg:scale-y-100 lg:-scale-x-100': index === 0,
                '-scale-x-100 lg:-scale-x-100 lg:-scale-y-100': index === 1,
              }"
            >
              <div
                class="rotating-gradient-element w-58 h-58 lg:w-130 lg:h-130"
              />
            </div>
          </div>
        </div>

        <div
          class="flex gap-2.5 flex-row lg:flex-col"
          :class="{
            'lg:gap-2': isRuLocale,
            'lg:gap-15': isNotRuLocale,
          }"
        >
          <div
            v-for="item in belongTransforms.slice(2, 4)"
            :key="item.id"
            class="bg-custom-gradient h-auto text-center flex w-full max-w-50 px-2.5 min-[425px]:px-3.5 flex-col pb-1.5 lg:flex-col lg:items-start lg:text-left lg:![background:none] lg:px-0 w-full rounded-lg"
          >
            <NuxtImg
              loading="lazy"
              format="webp"
              :src="item.image"
              alt=""
              class="w-25 h-25 max-lg:mx-auto"
            />
            <div class="flex flex-col h-full lg:gap-2">
              <p
                class="uppercase font-semibold text-sm tracking-wide leading-tight break-all"
                :class="{
                  'min-[425px]:text-lg': isRuLocale,
                  'min-[425px]:text-xl': isNotRuLocale,
                }"
              >
                {{ item.title }}
              </p>
              <p
                class="text-[#969696] h-full flex text-sm items-center leading-tight tracking-wide text-xs min-[425px]:text-base break-words"
                style="word-break: break-word"
              >
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-7.5 px-5">
      <div
        class="flex flex-col gap-3 text-center lg:flex-row lg:items-end lg:justify-center lg:gap-33.5 lg:pt-22"
      >
        <div class="flex flex-col gap-16.5">
          <div class="flex flex-col lg:items-start lg:gap-3">
            <p class="font-medium text-xl tracking-wide uppercase">
              {{ t('use-cases') }}
            </p>
            <p
              class="font-semibold text-[1.5rem] tracking-wide leading-6.5 lg:text-[2rem] lg:leading-normal"
              style="
                background: linear-gradient(
                    90.03deg,
                    #4d61fd -5.37%,
                    #8247df 48.86%,
                    #bb2bbf 104.19%
                  )
                  text;
                -webkit-text-fill-color: transparent;
              "
            >
              {{ t('one-key-to-access-everything') }}
            </p>
          </div>

          <div class="flex flex-col gap-5">
            <NuxtImg
              loading="lazy"
              format="webp"
              class="max-w-97.5 w-full mx-auto lg:hidden"
              src="/images/home/use_cases_key.png"
              alt=""
            />

            <div
              class="flex flex-col gap-2 mx-auto lg:flex-row lg:flex-wrap lg:max-w-164.5"
            >
              <div
                v-for="item in useCases"
                :key="item.id"
                class="flex flex-row items-center text-start gap-6 rounded-lg max-w-97.5 pl-4 pr-10 h-16 lg:flex-col lg:justify-between lg:h-52.5 lg:w-full lg:max-w-52.5 lg:items-start lg:py-6 lg:pl-6 lg:pr-8.5"
                style="
                  background: linear-gradient(
                    218.16deg,
                    #19161c -8.71%,
                    #212121 94.29%
                  );
                "
                data-aos="fade-right"
                data-aos-easing="ease-out-cubic"
              >
                <NuxtImg
                  :src="item.image"
                  :alt="item.title"
                  class="min-w-10 max-w-10 w-10 h-10 lg:min-w-12.5 lg:w-12.5 lg:h-12.5"
                />

                <p
                  class="font-light text-base leading-5 tracking-wide lg:text-xl lg:leading-5.5"
                >
                  {{ item.title }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="relative max-[1079.9px]:mb-15">
          <NuxtImg
            loading="lazy"
            format="webp"
            class="min-w-65 w-65 max-w-65 mx-auto hidden lg:flex lg:h-130 lg:object-cover lg:mx-0"
            src="/images/home/use_cases_key_desktop.png"
            alt=""
          />

          <NuxtImg
            loading="lazy"
            format="webp"
            src="/images/home/use_cases_loading.png"
            class="w-26.5 h-26.5 absolute left-0 right-0 mx-auto bottom-43.5 rotating-element hidden object-contain lg:flex"
            alt=""
          />
        </div>
      </div>
    </div>

    <div class="relative py-7.5 px-5">
      <Icon
        name="belong:belong-product"
        class="max-w-96 w-full h-15 flex mx-auto bg-contain xl:max-w-360 xl:h-75 xl:bg-bottom xl:translate-y-10"
      />

      <div class="flex flex-row relative xl:max-w-288.5 xl:mx-auto">
        <div
          class="flex flex-col gap-3 mt-7.5 max-xl:absolute max-xl:z-1 max-xl:left-0 max-xl:right-0 max-xl:items-center"
        >
          <div
            v-for="(item, index) in product"
            :key="item.id"
            class="flex flex-row items-center max-w-94 px-3.5 gap-5 rounded-lg z-9 w-full backdrop-blur-lg"
            :style="{ zIndex: -index }"
            style="
              background: linear-gradient(
                80.34deg,
                #222222 32.04%,
                rgba(0, 0, 0, 0.1) 89.47%
              );
            "
            data-aos="fade-down"
            data-aos-easing="ease-out-cubic"
            data-aos-duration="800"
          >
            <NuxtImg
              :src="item.image"
              :alt="item.title"
              class="min-w-13 w-13 h-13"
            />

            <p
              class="text-base font-light tracking-wide leading-tight py-5 max-[375px]:text-sm"
            >
              {{ item.title }}
            </p>
          </div>
        </div>

        <NuxtImg
          loading="lazy"
          format="webp"
          class="w-65 h-130.5 -translate-y-3.3 xl:translate-y-0 mx-auto"
          src="/images/home/belong_product.png"
          alt=""
        />

        <div class="flex-col gap-3 mt-7.5 hidden xl:flex xl:static">
          <div
            v-for="(item, index) in product1"
            :key="item.id"
            class="flex flex-row items-center max-w-94 px-3.5 gap-5 rounded-lg z-9 w-full backdrop-blur-lg"
            :style="{ zIndex: -index }"
            style="
              background: linear-gradient(
                218.16deg,
                #19161c -8.71%,
                #212121 94.29%
              );
            "
            data-aos="fade-down"
            data-aos-easing="ease-out-cubic"
            data-aos-duration="800"
          >
            <NuxtImg
              :src="item.image"
              :alt="item.title"
              class="min-w-13 w-13 h-13"
            />
            <p class="text-base font-light tracking-wide leading-tight py-5">
              {{ item.title }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      class="px-3.5 relative max-w-232.5 mx-auto lg:px-20 lg:mt-33.5 lg:max-w-244 xl:px-0"
    >
      <div
        class="flex flex-row justify-between text-[#595959] text-xl tracking-wide uppercase"
      >
        <p class="font-medium mb-5 lg:pl-9.5">{{ t('roadmap') }}</p>
        <p class="font-light hidden lg:flex">{{ t('2021-2025') }}</p>
      </div>
      <div
        v-for="(yearData, yearIndex) in INN_UTIL"
        :key="`year-${yearData.year}-${yearIndex}`"
        class="flex flex-row gap-2.5 lg:gap-52.5"
      >
        <div class="lg:min-w-76.5">
          <div class="sticky top-10">
            <div class="flex flex-row items-center">
              <div class="bg-[#151515] p-0.5">
                <div class="border-0.125 rounded-full border-[#6A3CD4]">
                  <div class="border-4 border-[#151515] rounded-full">
                    <div
                      class="w-1.5 h-1.5 bg-[#FF9BF5] rounded-full"
                      style="box-shadow: 0px 0px 4px 0px #ff9bf580"
                    />
                  </div>
                </div>
              </div>
              <div
                class="flex flex-row items-center justify-between w-full lg:pl-4 lg:flex-col lg:items-start"
              >
                <p
                  class="text-xl leading-6.5 lg:text-[2.75rem] lg:leading-normal font-bold tracking-wide"
                  style="
                    background: linear-gradient(
                        107.92deg,
                        #4d61fd 37.78%,
                        #bb2bbf 106.06%
                      )
                      text;
                    -webkit-text-fill-color: transparent;
                  "
                >
                  {{ yearData.year }}
                </p>
                <p
                  class="hidden lg:flex lg:font-light lg:uppercase lg:tracking-[.075rem] lg:text-xl lg:leading-6.5"
                >
                  {{ yearData.title }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-8 pb-10 w-full">
          <p
            class="text-base font-light text-right leading-7 uppercase tracking-[.075rem] lg:hidden"
          >
            {{ yearData.title }}
          </p>
          <div
            v-for="(items, quarter) in yearData.quarters"
            :key="`q-${yearData.year}-${quarter}`"
            class="flex flex-col items-start gap-3.5 lg:gap-10"
          >
            <p
              v-if="String(quarter) !== '0'"
              class="bg-[#212121] rounded-xs text-[#D9D9D9] font-medium text-xl h-8 text-center"
              :style="{
                width:
                  quarter === 'Q1Q2' || quarter === 'Q3Q4' || quarter === 'Q2Q3'
                    ? '106px'
                    : '68px',
              }"
            >
              {{
                quarter === 'Q1Q2'
                  ? 'Q1-Q2'
                  : quarter === 'Q2Q3'
                  ? 'Q2-Q3'
                  : quarter === 'Q3Q4'
                  ? 'Q3-Q4'
                  : quarter
              }}
            </p>
            <div class="flex flex-col gap-3.5">
              <div
                v-for="(item, itemIndex) in items"
                :key="`i-${yearData.year}-${quarter}-${itemIndex}`"
                class="flex flex-row items-center gap-2"
              >
                <Icon
                  v-if="item.checked === true"
                  name="belong:checkbox"
                  class="min-w-5.5 w-5.5 h-5.25"
                />
                <div
                  v-else-if="item.checked === false"
                  class="bg-[#212121] min-w-5 w-5 h-5 rounded-0.75 mr-0.5"
                />

                <p
                  class="text-[#C5C5C5] font-light text-base tracking-[.075rem]"
                >
                  {{ item.text }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="w-0.5 h-full absolute top-6 left-5.5 -z-1 lg:left-22 lg:top-0 xl:left-2"
        style="
          background: linear-gradient(
            180deg,
            rgba(117, 65, 237, 0) 0%,
            #7541ed 7.87%,
            rgba(117, 65, 237, 0.98) 93.27%,
            rgba(117, 65, 237, 0) 100%
          );
        "
      >
        <div class="absolute top-0 bg-[#6827FF] w-0.5 transition-all"></div>
      </div>
    </div>

    <Cases />

    <div>
      <p class="font-medium text-xl tracking-wide text-center uppercase">
        {{ t('collabs-and-community') }}
      </p>
    </div>

    <div class="mx-auto overflow-hidden pt-10 pb-18">
      <div class="flex flex-row gap-9 transform -translate-x-10">
        <div
          v-for="i in 28"
          class="min-w-15 min-h-15 max-w-15 w-full max-h-15 h-full bg-gradient-to-b from-[#272727] to-[#151515] rounded-full"
        />
      </div>

      <div class="carousel overflow-hidden flex mx-auto relative mt-5.5">
        <div
          v-for="(i, index) in 2"
          class="w-30 h-full absolute top-0 m-auto bg-gradient-to-r z-9"
          :class="{
            'left-0 from-[#151515] to-transparent': index === 0,
            'right-0 to-[#151515] from-transparent': index === 1,
          }"
        />

        <div class="flex flex-col items-center gap-7.5 pr-1.5">
          <div
            v-for="(item, index) in collabsAndCommunity"
            class="flex flex-row"
          >
            <div
              v-for="i in 3"
              class="flex flex-row items-center gap-1.5 pr-1.5"
              :class="{
                'carousel-track': index === 0,
                'carousel-track-reverse': index === 1,
              }"
            >
              <div
                v-for="collab in item.collabs"
                class="flex flex-col items-center gap-1.5 w-25"
              >
                <NuxtImg
                  loading="lazy"
                  :src="collab.image"
                  class="object-contain w-15 h-15"
                  :alt="collab.name"
                />
                <p
                  class="text-sm tracking-wide text-[#969696] text-center leading-4.25 break-words"
                >
                  {{ collab.name }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-row gap-9 transform -translate-x-10 pt-4">
        <div
          v-for="i in 28"
          class="min-w-15 min-h-15 max-w-15 w-full max-h-15 h-full bg-gradient-to-b from-[#272727] to-[#151515] rounded-full"
        />
      </div>
    </div>

    <div
      class="max-w-255.5 min-h-61.5 rounded-[0.875rem] overflow-hidden relative mx-5 lg:mx-auto"
    >
      <NuxtImg
        loading="lazy"
        format="webp"
        src="/images/home/event_ticketing.png"
        alt=""
        class="w-full h-full absolute -z-1 left-0 right-0"
      />

      <div class="flex flex-col gap-16.5 px-15 pt-10 pb-14">
        <div
          class="flex flex-row flex-wrap justify-center text-center items-center text-xl lg:text-[1.75rem] gap-3.5"
        >
          {{ t('event-ticketing') }}

          <Icon name="belong:arrow-ticketing" class="w-16 h-4" />

          {{ t('community-engagement-and-monetization') }}
        </div>

        <div
          class="flex flex-wrap items-center gap-5 justify-center lg:justify-center"
        >
          <ReusablesInstallButtons />

          <NuxtLink :to="appLink" target="_blank" :aria-label="t('web-app')">
            <div
              class="p-0.25 rounded-full overflow-hidden"
              style="
                background: linear-gradient(
                  85.11deg,
                  #7540ee -2.65%,
                  #4467ff 47.09%,
                  #ff8fe9 93.65%
                );
              "
            >
              <div
                class="bg-[#000000] rounded-full flex flex-row items-center gap-2 py-2.5 px-3.5"
              >
                <Icon name="belong:web-app" class="w-5 h-5" />
                <p class="uppercase font-medium text-sm tracking-wide">
                  {{ t('web-app') }}
                </p>
              </div>
            </div>
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@reference "tailwindcss";

.swiper-pagination-bullets {
  @apply !bottom-0;
}

.swiper-pagination-bullet {
  @apply bg-[#212121] w-4 h-4;
}

.swiper-pagination-bullet-active {
  @apply bg-[#6827FF] border-4 border-[#212121];
}

swiper-container {
  &::part(bullet-active) {
    @apply bg-[#6827fffa] border-4 border-solid border-[#1d1d1d];
  }
  &::part(bullet) {
    @apply bg-[#3c3c3c] border-4 border-solid border-[#3c3c3c];
  }
  &::part(container) {
    @apply pb-8.5;
  }
  &::part(pagination) {
    @apply bottom-0;
  }
}

.masked-element {
  -webkit-mask-image: url('/images/home/mask.svg');
  mask-image: url('/images/home/mask.svg');
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position-y: bottom;
  -webkit-mask-position-x: right;
}

@media (min-width: 1023.9px) {
  .masked-element {
    -webkit-mask-image: url('/images/home/mask-desktop.svg');
    mask-image: url('/images/home/mask-desktop.svg');
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
  }
}

.rotating-gradient-element {
  background: linear-gradient(0deg, #230e53, #7857c2);
  animation-name: rotate-animation;
  animation-duration: 2s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes rotate-animation {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.rotating-element {
  animation-name: rotate-animation-loop;
  animation-duration: 1.5s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes rotate-animation-loop {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.bg-custom-gradient {
  background: linear-gradient(
    148.42deg,
    rgba(13, 13, 13, 0.06) -18.27%,
    rgba(78, 78, 78, 0.2) 80.96%
  );
}

.feature-gradient::before {
  @apply rounded-[1.25rem];
  background: linear-gradient(
    104.04deg,
    rgba(255, 255, 255, 0.2) 22.71%,
    rgba(255, 255, 255, 0.5) 41.98%,
    rgba(255, 255, 255, 0.2) 55.52%
  );
}
</style>
